import { el, tel } from "./dom.ts";
import type { TranslationKey } from "./i18n.ts";

/**
 * Help: FAQ + tips, as a single collapsible block at the bottom of the page.
 *
 * Deliberately scoped to what the app can actually do today. ARCHITECTURE.md
 * describes a much larger eventual feature set (date-range filters, richer
 * viewer, export.log); answering as if those already existed would mislead
 * the person reading this while those modules are still unbuilt.
 *
 * It used to be an always-open sidebar taking a whole column; demoted here to
 * one closed `<details>` so the main flow (sign in → choose → export → browse)
 * is what the page is about, and reference material is one click away rather
 * than competing for attention.
 *
 * <details>/<summary> so collapse/expand, keyboard operation and
 * screen-reader semantics come from the browser rather than from ARIA
 * reimplemented over <div>s.
 */

const FAQ_ENTRIES: readonly [TranslationKey, TranslationKey][] = [
    ["faq.q.dataLocation", "faq.a.dataLocation"],
    ["faq.q.browsers", "faq.a.browsers"],
    ["faq.q.mobile", "faq.a.mobile"],
    ["faq.q.perModuleConsent", "faq.a.perModuleConsent"],
    ["faq.q.interruption", "faq.a.interruption"],
    ["faq.q.permissionPopup", "faq.a.permissionPopup"],
    ["faq.q.repeatExport", "faq.a.repeatExport"]
];

const TIP_KEYS: readonly TranslationKey[] = [
    "tips.emptyFolder",
    "tips.keepTabOpen",
    "tips.checkSpace",
    "tips.samefolder",
    "tips.oneModuleAtATime"
];

function faqEntry(questionKey: TranslationKey, answerKey: TranslationKey): HTMLElement {
    return el("details", { class: "faq-entry" }, [tel("summary", questionKey), tel("p", answerKey)]);
}

export function createHelpPanel(): HTMLElement {
    const entries = FAQ_ENTRIES.map(([q, a]) => faqEntry(q, a));
    const tips = TIP_KEYS.map((key) => tel("li", key));

    const body = el("div", { class: "help-body" }, [
        el("div", { class: "help-col" }, [tel("h3", "faq.heading", { class: "help-heading" }), ...entries]),
        el("div", { class: "help-col" }, [
            tel("h3", "tips.heading", { class: "help-heading" }),
            el("ul", { class: "tips-list" }, tips)
        ])
    ]);

    return el("details", { class: "help-panel" }, [tel("summary", "help.heading", { class: "help-summary" }), body]);
}
